<template>
  <div class="parameters-display">
    <!-- Parameters Table -->
    <table class="parameters-table table table-sm table-bordered">
      <thead>
        <tr>
          <th>
            <translate translate-context="email_templates">Parameter</translate>
          </th>
          <th>
            <translate translate-context="email_templates">Description / Value</translate>
          </th>
        </tr>
      </thead>
      <tbody>
        <!-- Parameters (expanded for MailUp arrays) -->
        <tr v-for="(param, index) in expandedParameters" :key="index" :class="'param-row-' + param.type">
          <td :class="{'pl-4': param.type === 'array-pattern'}">
            <div class="d-flex align-items-center">
              <CIcon
                v-if="param.type !== 'array-header'"
                name="cil-copy"
                size="sm"
                class="copy-icon mr-2"
                @click="copyToClipboard(formatParameter(param.key))"
                v-c-tooltip="$gettext('Copy to clipboard')"/>
              <CIcon
                v-else
                name="cil-folder-open"
                size="sm"
                class="mr-2 text-primary"/>
              <code class="parameter-key" :class="{'font-weight-bold': param.type === 'array-header'}">
                {{ formatParameter(param.key) }}
              </code>
            </div>
          </td>
          <td class="text-muted">
            {{ param.description }}
          </td>
        </tr>

        <!-- Translation Keys -->
        <tr v-for="key in translationKeys" :key="'t_' + key">
          <td>
            <div class="d-flex align-items-center">
              <CIcon
                name="cil-copy"
                size="sm"
                class="copy-icon mr-2"
                @click="copyToClipboard(formatTranslation(key))"
                v-c-tooltip="$gettext('Copy to clipboard')"/>
              <code class="parameter-key parameter-key-translation">{{ formatTranslation(key) }}</code>
            </div>
          </td>
          <td class="text-muted">
            <slot name="translation-preview" :translationKey="key"></slot>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- MailUp Array Pattern Explanation (only if arrays exist) -->
    <CAlert v-if="hasArrayParameters" color="info" class="mt-3 mb-0">
      <div class="d-flex align-items-start">
        <CIcon name="cilLightbulb" size="lg" class="mr-3 flex-shrink-0 mt-1"/>
        <div>
          <strong>
            <translate translate-context="email_templates">How to use array parameters with MailUp</translate>
          </strong>
          <p class="mb-0 mt-2 small">
            <translate translate-context="email_templates">
              Replace <strong>X</strong> with the index number (1, 2, 3, ...) to access items in the array.
              For example, to show the first 3 items in your cart:
            </translate>
          </p>
          <ul class="mb-0 mt-2 small">
            <li><code>[items_1_event_name]</code>, <code>[items_1_price]</code> - First item</li>
            <li><code>[items_2_event_name]</code>, <code>[items_2_price]</code> - Second item</li>
            <li><code>[items_3_event_name]</code>, <code>[items_3_price]</code> - Third item</li>
          </ul>
        </div>
      </div>
    </CAlert>
  </div>
</template>

<script>
export default {
  name: 'MailUpParametersDisplay',
  props: {
    availableParameters: {
      type: Object,
      required: true,
      default: () => ({})
    },
    translationKeys: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  computed: {
    hasArrayParameters() {
      // Check if there are any array parameters (containing [])
      return Object.keys(this.availableParameters).some(key => key.includes('[]'))
    },
    expandedParameters() {
      const params = this.availableParameters
      const result = []
      const arrayGroups = {}

      // Group array parameters
      Object.entries(params).forEach(([key, description]) => {
        if (key.includes('[]')) {
          const baseKey = key.split('[]')[0]
          const suffix = key.split('[]')[1] || ''

          if (!arrayGroups[baseKey]) {
            arrayGroups[baseKey] = {
              base: null,
              fields: []
            }
          }

          if (suffix === '') {
            // This is the base array key
            arrayGroups[baseKey].base = { key, description }
          } else {
            // This is a field of the array
            arrayGroups[baseKey].fields.push({ suffix, description })
          }
        } else {
          // Simple parameter
          result.push({ key, description, type: 'simple' })
        }
      })

      // Convert array groups to display structure
      Object.entries(arrayGroups).forEach(([baseKey, group]) => {
        // Add array header
        if (group.base) {
          result.push({
            key: group.base.key,
            description: group.base.description,
            type: 'array-header'
          })
        }

        // Add field patterns with X placeholder
        group.fields.forEach(field => {
          result.push({
            key: `${baseKey}[X]${field.suffix}`,
            description: field.description,
            type: 'array-pattern'
          })
        })
      })

      return result
    }
  },
  methods: {
    formatParameter(key) {
      // MailUp: [code] - Convert item[x].name to item_x_name
      const formattedKey = key
        .replace(/\[/g, '_')
        .replace(/\]/g, '')
        .replace(/\./g, '_')
        .replace(/_+/g, '_') // Replace multiple underscores with single
        .replace(/^_|_$/g, '') // Remove leading/trailing underscores
      return '[' + formattedKey + ']'
    },
    formatTranslation(key) {
      // MailUp: [t_key]
      return '[t_' + key + ']'
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.notifySuccess(this.$pgettext('email_templates', 'Copied to clipboard'))
      }).catch(() => {
        this.notifyError(this.$pgettext('email_templates', 'Failed to copy to clipboard'))
      })
    }
  }
}
</script>

<style scoped>
.parameters-display {
  width: 100%;
}

.parameters-table {
  background: white;
  margin-bottom: 0;
}

.parameters-table th {
  background: #e9ecef;
  font-weight: 600;
  color: #495057;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}

.parameters-table td {
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}

.parameter-key {
  background: #f1f3f5;
  color: #e83e8c;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-size: 0.875rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.parameter-key-translation {
  color: #6610f2;
}

.copy-icon {
  cursor: pointer;
  color: #6c757d;
  transition: color 0.15s;
}

.copy-icon:hover {
  color: #007bff;
}

/* Array Parameter Rows */
.param-row-array-header {
  background: #f8f9fa;
  border-left: 3px solid #007bff;
}

.param-row-array-header td {
  font-weight: 600;
  padding-top: 0.85rem !important;
  padding-bottom: 0.4rem !important;
}

.param-row-array-pattern {
  background: #fcfcfc;
}

.param-row-array-pattern td {
  padding-top: 0.5rem !important;
  padding-bottom: 0.5rem !important;
}
</style>
